import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Calendar, Clock, MapPin, Phone, User, ArrowRight } from 'lucide-react';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function BookingConfirmation() {
  const { bookingId } = useParams();
  const { theme } = useTheme();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem('zenvy_tokens') || '{}');
      const headers = tokens.access_token ? { 'Authorization': `Bearer ${tokens.access_token}` } : {};
      
      const response = await fetch(`${API}/bookings/${bookingId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      }
    } catch (error) {
      console.error('Failed to fetch booking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isMen = theme === 'men';
  const bgClass = isMen ? 'bg-zinc-950' : 'bg-rose-50';
  const textClass = isMen ? 'text-white' : 'text-rose-900';
  const secondaryTextClass = isMen ? 'text-zinc-400' : 'text-rose-600';
  const cardBgClass = isMen ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-pink-100';
  const accentClass = isMen ? 'text-blue-500' : 'text-pink-600';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      <Navbar />

      <div className="pt-24 pb-20 px-4 max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8 animate-slideUp">
          <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${isMen ? 'bg-blue-600/20' : 'bg-pink-600/20'}`}>
            <Check className={`w-12 h-12 ${accentClass}`} />
          </div>
          <h1 className={`text-3xl font-bold ${textClass} mb-2`} data-testid="confirmation-title">
            Booking Confirmed!
          </h1>
          <p className={secondaryTextClass}>Your appointment has been successfully scheduled</p>
        </div>

        {booking && (
          <Card className={`${cardBgClass} mb-8`} data-testid="booking-details">
            <CardContent className="p-6 space-y-6">
              <div className={`pb-6 border-b ${isMen ? 'border-zinc-800' : 'border-pink-100'}`}>
                <span className={`text-sm ${secondaryTextClass}`}>Booking ID</span>
                <p className={`font-mono text-lg ${textClass}`}>{booking.booking_id}</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className={`w-4 h-4 ${accentClass}`} />
                    <span className={`text-sm ${secondaryTextClass}`}>Salon</span>
                  </div>
                  <p className={`font-medium ${textClass}`}>{booking.salon_name}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className={`w-4 h-4 ${accentClass}`} />
                    <span className={`text-sm ${secondaryTextClass}`}>Date</span>
                  </div>
                  <p className={`font-medium ${textClass}`}>{booking.booking_date}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className={`w-4 h-4 ${accentClass}`} />
                    <span className={`text-sm ${secondaryTextClass}`}>Time</span>
                  </div>
                  <p className={`font-medium ${textClass}`}>{booking.time_slot}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <User className={`w-4 h-4 ${accentClass}`} />
                    <span className={`text-sm ${secondaryTextClass}`}>Customer</span>
                  </div>
                  <p className={`font-medium ${textClass}`}>{booking.customer_name}</p>
                </div>
              </div>

              <div className={`pt-6 border-t ${isMen ? 'border-zinc-800' : 'border-pink-100'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className={`text-sm ${secondaryTextClass}`}>Service</p>
                    <p className={`font-medium ${textClass}`}>{booking.service_name}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${secondaryTextClass}`}>Total</p>
                    <p className={`text-2xl font-bold ${accentClass}`}>${booking.total_price}</p>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${isMen ? 'bg-blue-600/10' : 'bg-pink-600/10'}`}>
                <p className={`text-sm ${accentClass}`}>
                  Status: <span className="font-semibold uppercase">{booking.status}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/">
            <Button variant="outline" className={`w-full sm:w-auto ${isMen ? 'border-zinc-700 text-white' : 'border-pink-200 text-rose-900'}`}>
              Back to Home
            </Button>
          </Link>
          <Link to="/salons">
            <Button className={`w-full sm:w-auto ${isMen ? 'bg-blue-600 hover:bg-blue-700' : 'bg-pink-600 hover:bg-pink-700'} text-white`}>
              Browse More Salons <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
